import apiService from '../services/apiService';
import Lead from '../models/Lead';

/**
 * Lead Controller
 * Handles all business logic for lead operations
 */

export const leadController = {
  // Get all leads with optional filters
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `leads?${queryParams}` : 'leads';
      
      const response = await apiService.get(endpoint);
      
      // Convert API response to Lead model instances
      const leads = response.data?.map(lead => Lead.fromJSON(lead)) || [];
      
      return {
        success: true,
        data: leads,
        total: response.total || leads.length,
        message: 'Leads fetched successfully'
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        message: error.message || 'Failed to fetch leads',
        error
      };
    }
  },

  // Get lead by ID
  getById: async (id) => {
    try {
      const response = await apiService.get(`leads/${id}`);
      const lead = Lead.fromJSON(response.data);
      
      return {
        success: true,
        data: lead,
        message: 'Lead fetched successfully'
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.message || 'Failed to fetch lead',
        error
      };
    }
  },

  // Create new lead
  create: async (leadData) => {
    try {
      // Create Lead instance for validation
      const lead = new Lead(leadData);
      
      // Validate
      const validation = lead.validate();
      if (!validation.isValid) {
        return {
          success: false,
          message: 'Validation failed',
          errors: validation.errors
        };
      }
      
      // Send to API
      const response = await apiService.post('leads', lead.toJSON());
      const createdLead = Lead.fromJSON(response.data);
      
      return {
        success: true,
        data: createdLead,
        message: 'Lead created successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to create lead',
        error
      };
    }
  },

  // Update existing lead
  update: async (id, leadData) => {
    try {
      // Create Lead instance for validation
      const lead = new Lead({ ...leadData, id });
      
      // Validate
      const validation = lead.validate();
      if (!validation.isValid) {
        return {
          success: false,
          message: 'Validation failed',
          errors: validation.errors
        };
      }
      
      // Send to API
      const response = await apiService.put(`leads/${id}`, lead.toJSON());
      const updatedLead = Lead.fromJSON(response.data);
      
      return {
        success: true,
        data: updatedLead,
        message: 'Lead updated successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to update lead',
        error
      };
    }
  },

  // Delete lead
  delete: async (id) => {
    try {
      await apiService.delete(`leads/${id}`);
      
      return {
        success: true,
        message: 'Lead deleted successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to delete lead',
        error
      };
    }
  },

  // Convert lead to opportunity
  convertToOpportunity: async (id, opportunityData) => {
    try {
      const response = await apiService.post(`leads/${id}/convert`, opportunityData);
      
      return {
        success: true,
        data: response.data,
        message: 'Lead converted to opportunity successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to convert lead',
        error
      };
    }
  },

  // Bulk import leads
  bulkImport: async (leadsData) => {
    try {
      const response = await apiService.post('leads/bulk-import', { leads: leadsData });
      
      return {
        success: true,
        data: response.data,
        message: `${response.data.imported} leads imported successfully`,
        stats: response.data
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to import leads',
        error
      };
    }
  },

  // Export leads
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`leads/export?${queryParams}`);
      
      return {
        success: true,
        data: response.data,
        message: 'Leads exported successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to export leads',
        error
      };
    }
  },

  // Get lead statistics
  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `leads/statistics?${queryParams}` : 'leads/statistics';
      
      const response = await apiService.get(endpoint);
      
      return {
        success: true,
        data: response.data,
        message: 'Statistics fetched successfully'
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.message || 'Failed to fetch statistics',
        error
      };
    }
  },

  // Update lead status
  updateStatus: async (id, status) => {
    try {
      const response = await apiService.patch(`leads/${id}/status`, { status });
      const updatedLead = Lead.fromJSON(response.data);
      
      return {
        success: true,
        data: updatedLead,
        message: 'Status updated successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to update status',
        error
      };
    }
  },

  // Assign lead to user
  assign: async (id, userId) => {
    try {
      const response = await apiService.patch(`leads/${id}/assign`, { userId });
      const updatedLead = Lead.fromJSON(response.data);
      
      return {
        success: true,
        data: updatedLead,
        message: 'Lead assigned successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to assign lead',
        error
      };
    }
  }
};

export default leadController;